import {
  incidentPathSchema,
  incidentStatusCommandSchema,
  incidentPriorityCommandSchema,
  incidentTeamCommandSchema,
  incidentIdempotencyKeySchema,
} from '@solidops/shared/incidents';
import type {
  IncidentDetailDto,
  IncidentPriorityCommandDto,
  IncidentTeamCommandDto,
} from '@solidops/shared/incidents';

import {
  acknowledgeIncident as acknowledgeIncidentRequest,
  changeIncidentPriority as changeIncidentPriorityRequest,
  changeIncidentTeam as changeIncidentTeamRequest,
  resolveIncident as resolveIncidentRequest,
} from './generated/incidents/incidents.js';

export type IncidentVersionedCommandAuth = {
  readonly csrfToken: string;
  readonly idempotencyKey: string;
};

export type IncidentCommandOptions = { readonly signal?: AbortSignal };

/** Status commands carry no caller-controlled actor, clock, status, or retry identity. */
export async function requestIncidentAcknowledgement(
  incidentId: string,
  csrfToken: string,
  options?: IncidentCommandOptions,
): Promise<IncidentDetailDto> {
  const path = incidentPathSchema.parse({ incidentId });
  return acknowledgeIncidentRequest(path.incidentId, incidentStatusCommandSchema.parse({}), {
    headers: { 'x-csrf-token': csrfToken },
    ...(options?.signal ? { signal: options.signal } : {}),
  });
}

export async function requestIncidentResolution(
  incidentId: string,
  csrfToken: string,
  options?: IncidentCommandOptions,
): Promise<IncidentDetailDto> {
  const path = incidentPathSchema.parse({ incidentId });
  return resolveIncidentRequest(path.incidentId, incidentStatusCommandSchema.parse({}), {
    headers: { 'x-csrf-token': csrfToken },
    ...(options?.signal ? { signal: options.signal } : {}),
  });
}

export async function requestIncidentPriorityChange(
  incidentId: string,
  input: IncidentPriorityCommandDto,
  auth: IncidentVersionedCommandAuth,
  options?: IncidentCommandOptions,
): Promise<IncidentDetailDto> {
  const path = incidentPathSchema.parse({ incidentId });
  const body = incidentPriorityCommandSchema.parse(input);
  const key = incidentIdempotencyKeySchema.parse(auth.idempotencyKey);
  return changeIncidentPriorityRequest(path.incidentId, body, {
    headers: { 'x-csrf-token': auth.csrfToken, 'Idempotency-Key': key },
    ...(options?.signal ? { signal: options.signal } : {}),
  });
}

export async function requestIncidentTeamChange(
  incidentId: string,
  input: IncidentTeamCommandDto,
  auth: IncidentVersionedCommandAuth,
  options?: IncidentCommandOptions,
): Promise<IncidentDetailDto> {
  const path = incidentPathSchema.parse({ incidentId });
  const body = incidentTeamCommandSchema.parse(input);
  const key = incidentIdempotencyKeySchema.parse(auth.idempotencyKey);
  return changeIncidentTeamRequest(path.incidentId, body, {
    headers: { 'x-csrf-token': auth.csrfToken, 'Idempotency-Key': key },
    ...(options?.signal ? { signal: options.signal } : {}),
  });
}
